import DocumentsPresenter from "../../documents/presenter/documents.js";
import ProductReviewsPresenter from "../../product-reviews/presenter/product-reviews.js";

import TabsListView from "../view/list.js";
import TabLinkView from "../view/link.js";
import RelatedSectionView from "../view/section.js";
import ScrollContainerNavigationView from "../../scroll-container-navigation/view/scroll-container-navigation.js";
import ToPropertiesButtonView from "../view/to-properties-button.js";

import ProductTabsModel from "../model/product-tabs.js";

import Animation from "../../animation/animation.js";
import ScrollToElement from "../../scroll-to-element/scroll-to-element.js";

import {
  defaultTabsModelData,
  ActionType,
  TOUCH_THRESHOLD,
  NavDirection,
  SectionId
} from "../const.js";

import {
  debounce
} from "../../../utils/debounce.js";

import {
  render,
  removeElement
} from "../../render/render.js";

import {
  ItemType
} from "../../scroll-container-navigation/const.js";

export default class ProductTabs {
  constructor(listElement, toPropertiesButton) {
    this._listElement = listElement;
    this._toPropertiesButton = toPropertiesButton;

    this._animation = new Animation();
    this._scrollToElement = new ScrollToElement();

    this._documentsPresenter = null;
    this._reviewsPresenter = null;

    this._tabsListView = null;
    this._tabsLinksViews = [];
    this._relatedSectionsViews = [];
    this._scrollContainerNavigationView = null;
    this._toPropertiesButtonView = null;

    this._tabsModel = null;

    this._handleTabLinkClick = this._handleTabLinkClick.bind(this);
    this._handlePrevButtonClick = this._handlePrevButtonClick.bind(this);
    this._handleNextButtonClick = this._handleNextButtonClick.bind(this);
    this._handleChangeData = this._handleChangeData.bind(this);
    this._handleWindowLoad = this._handleWindowLoad.bind(this);
    this._handleWindowResize = this._handleWindowResize.bind(this);
    this._handleSliderWindowResize = this._handleSliderWindowResize.bind(this);
    this._handleDebouncedSliderWindowResize = debounce(this._handleSliderWindowResize);
    this._handleListTouchStart = this._handleListTouchStart.bind(this);
    this._handleListTouchEnd = this._handleListTouchEnd.bind(this);
    this._handleToPropertiesButtonClick = this._handleToPropertiesButtonClick.bind(this);
  }

  init() {
    this._tabsListView = new TabsListView(this._listElement);

    Array.from(this._tabsListView.getLinks())
      .forEach((item, index) => {
        const linkView = new TabLinkView(item, index);
        linkView.setClickHandler(this._handleTabLinkClick);
        this._tabsLinksViews.push(linkView);

        const href = linkView.getHref();

        const relatedSectionElement = document.querySelector(href);
        const relatedSectionView = new RelatedSectionView(relatedSectionElement);

        this._relatedSectionsViews.push(relatedSectionView);
      });

    const activeTabIndex = this._tabsLinksViews
      .findIndex((view) => view.isActive());

    this._tabsModel = new ProductTabsModel(Object.assign(
        {},
        defaultTabsModelData,
        {
          activeTabIndex
        }
    ));

    this._animation.fadeIn(
        this._relatedSectionsViews[this._tabsModel.getData().activeTabIndex].getElement()
    );

    this._tabsModel.addObserver(this._handleChangeData);

    if (this._toPropertiesButton) {
      this._toPropertiesButtonView = new ToPropertiesButtonView(this._toPropertiesButton);
      this._toPropertiesButtonView.setClickHandler(this._handleToPropertiesButtonClick);
    }

    window.addEventListener(`load`, this._handleWindowLoad);
    window.addEventListener(`resize`, debounce(this._handleWindowResize));
  }

  _getTabsLinksWidth() {
    return this._tabsLinksViews
      .map((view) => {
        const element = view.getElement();
        const parent = view.getParent();
        return element.offsetWidth + parseFloat(window.getComputedStyle(parent).marginRight);
      })
      .reduce((previousValue, currentValue) => {
        return previousValue + currentValue;
      }, 0);
  }

  _changeSliderAppearance() {
    const listStyles = window.getComputedStyle(this._tabsListView.getElement());
    const thresholdWidth = window.innerWidth - parseFloat(listStyles.paddingLeft) - parseFloat(listStyles.paddingRight);


    const tabLinksWidth = this._getTabsLinksWidth();

    if (tabLinksWidth > thresholdWidth && !this._tabsModel.getData().isSliderInitialized) {
      this._tabsModel.changeData(Object.assign(
          {},
          this._tabsModel.getData(),
          {
            isSliderInitialized: true
          }
      ), ActionType.INIT_SLIDER);
    }

    if (tabLinksWidth <= thresholdWidth && this._tabsModel.getData().isSliderInitialized) {
      this._tabsModel.changeData(Object.assign(
          {},
          this._tabsModel.getData(),
          {
            isSliderInitialized: false,
            disabledPrev: true,
            disabledNext: false
          }
      ), ActionType.DESTROY_SLIDER);
    }
  }

  _renderSliderNavigation() {
    this._scrollContainerNavigationView = new ScrollContainerNavigationView();
    render(this._tabsListView.getParent(), this._scrollContainerNavigationView.getElement());

    this._scrollContainerNavigationView.setPrevButtonClickHandler(this._handlePrevButtonClick);
    this._scrollContainerNavigationView.setNextButtonClickHandler(this._handleNextButtonClick);

    this._tabsListView.setTouchStartHandler(this._handleListTouchStart);
    this._tabsListView.setTouchEndHandler(this._handleListTouchEnd);

    window.addEventListener(`resize`, this._handleDebouncedSliderWindowResize);
  }

  _destroySliderNavigation() {
    const scrollContainerNavigationElement = this._scrollContainerNavigationView.getElement();
    this._scrollContainerNavigationView = null;

    this._animation.fadeOut(
        scrollContainerNavigationElement,
        null,
        () => {
          removeElement(scrollContainerNavigationElement);
        }
    );

    this._tabsListView.removeTouchStartHandler();
    this._tabsListView.removeTouchEndHandler();

    window.removeEventListener(`resize`, this._handleDebouncedSliderWindowResize);
  }

  _disableNavItem(navItemType) {
    if (navItemType === ItemType.NEXT) {
      this._scrollContainerNavigationView.disableItem(ItemType.NEXT);
    } else {
      this._scrollContainerNavigationView.disableItem(ItemType.PREV);
    }
  }

  _enableNavItem(navItemType) {
    if (navItemType === ItemType.NEXT) {
      this._scrollContainerNavigationView.enableItem(ItemType.NEXT);
    } else {
      this._scrollContainerNavigationView.enableItem(ItemType.PREV);
    }
  }

  _initDocuments() {
    this._tabsModel.changeData(Object.assign(
        {},
        this._tabsModel.getData(),
        {
          docsInitialized: true
        }
    ));

    this._documentsPresenter = new DocumentsPresenter(this._relatedSectionsViews[this._tabsModel.getData().activeTabIndex].getElement());
    this._documentsPresenter.init();
  }

  _initReviews() {
    this._tabsModel.changeData(Object.assign(
        {},
        this._tabsModel.getData(),
        {
          reviewsInitialized: true
        }
    ));

    this._reviewsPresenter = new ProductReviewsPresenter(this._relatedSectionsViews[this._tabsModel.getData().activeTabIndex].getElement());
    this._reviewsPresenter.init();
  }

  _processInitSlider() {
    this._tabsListView.addSliderClass();
    this._renderSliderNavigation();
  }

  _processDestroySlider() {
    this._tabsListView.removeSliderClass();
    this._destroySliderNavigation();
    this._tabsListView.translate(0);
  }

  _processNavigationItems() {
    if (!this._tabsModel.getData().isSliderInitialized) {
      return;
    }

    const scrollTabIndex = this._tabsModel.getData().scrollTabIndex;
    const styles = window.getComputedStyle(this._tabsListView.getElement());
    const listScrollWidth = this._tabsListView.getElement().scrollWidth;

    let activeTabLinkOffsetLeft = this._tabsLinksViews[scrollTabIndex].getElement().offsetLeft - parseFloat(styles.paddingLeft);

    const remnant = listScrollWidth - activeTabLinkOffsetLeft - parseFloat(styles.paddingRight);
    const listWidth = this._tabsListView.getElement().offsetWidth - parseFloat(styles.paddingRight) - parseFloat(styles.paddingLeft);

    if (remnant < listWidth) {
      activeTabLinkOffsetLeft -= listWidth - remnant;

      this._tabsModel.changeData(Object.assign(
          {},
          this._tabsModel.getData(),
          {
            disabledNext: true
          }
      ), ActionType.DISABLE_NAV_ITEM, {
        navItemType: ItemType.NEXT
      });
    } else {
      this._tabsModel.changeData(Object.assign(
          {},
          this._tabsModel.getData(),
          {
            disabledNext: false
          }
      ), ActionType.ENABLE_NAV_ITEM, {
        navItemType: ItemType.NEXT
      });
    }

    if (listScrollWidth - parseFloat(styles.paddingLeft) > remnant) {
      this._tabsModel.changeData(Object.assign(
          {},
          this._tabsModel.getData(),
          {
            disabledPrev: false
          }
      ), ActionType.ENABLE_NAV_ITEM, {
        navItemType: ItemType.PREV
      });
    } else {
      this._tabsModel.changeData(Object.assign(
          {},
          this._tabsModel.getData(),
          {
            disabledPrev: true
          }
      ), ActionType.DISABLE_NAV_ITEM, {
        navItemType: ItemType.PREV
      });
    }

    this._tabsListView.translate(activeTabLinkOffsetLeft);
  }


  _processSliderSlide() {
    this._processNavigationItems();
  }

  _handleSliderWindowResize() {
    this._processNavigationItems();
  }

  _handlePrevButtonClick(evt) {
    evt.preventDefault();

    const modelData = this._tabsModel.getData();
    const newScrollTabIndex = modelData.scrollTabIndex - 1;

    if (newScrollTabIndex < 0) {
      return;
    }

    this._tabsModel.changeData(Object.assign(
        {},
        modelData,
        {
          scrollTabIndex: newScrollTabIndex
        }
    ), ActionType.SLIDE_PREV);
  }

  _handleNextButtonClick(evt) {
    evt.preventDefault();

    const modelData = this._tabsModel.getData();
    const newScrollTabIndex = modelData.scrollTabIndex + 1;

    if (newScrollTabIndex >= this._tabsLinksViews.length) {
      return;
    }

    this._tabsModel.changeData(Object.assign(
        {},
        modelData,
        {
          scrollTabIndex: newScrollTabIndex
        }
    ), ActionType.SLIDE_NEXT);
  }

  _handleTabLinkClick(evt, index) {
    evt.preventDefault();

    if (index === this._tabsModel.getData().activeTabIndex) {
      return;
    }

    this._tabsModel.changeData(Object.assign(
        {},
        this._tabsModel.getData(),
        {
          activeTabIndex: index,
          oldActiveTabIndex: this._tabsModel.getData().activeTabIndex
        }
    ), ActionType.CHANGE);
  }

  _handleListTouchStart(evt) {
    if (evt.touches.length !== 1) {
      return;
    }

    const touch = evt.changedTouches[0];

    this._tabsModel.changeData(Object.assign(
        this._tabsModel.getData(),
        {
          isTouched: true,
          touchX: touch.pageX
        }
    ));
  }

  _handleListTouchEnd(evt) {
    if (evt.changedTouches.length !== 1) {
      return;
    }

    const modelData = this._tabsModel.getData();

    const touch = evt.changedTouches[0];
    const endTouchX = touch.pageX;
    const startTouchX = this._tabsModel.getData().touchX;
    const touchWidth = Math.abs(endTouchX - startTouchX);

    if (touchWidth > TOUCH_THRESHOLD) {
      const direction = (endTouchX > startTouchX) ?
        NavDirection.PREV :
        NavDirection.NEXT;

      if (direction === NavDirection.NEXT && modelData.disabledNext) {
        return;
      }

      if (direction === NavDirection.PREV && modelData.disabledPrev) {
        return;
      }

      const newScrollTabIndex = (direction === NavDirection.NEXT) ?
        modelData.scrollTabIndex + 1 :
        modelData.scrollTabIndex - 1;

      const action = (direction === NavDirection.NEXT) ?
        ActionType.SLIDE_NEXT :
        ActionType.SLIDE_PREV;

      this._tabsModel.changeData(Object.assign(
          {},
          modelData,
          {
            scrollTabIndex: newScrollTabIndex
          }
      ), action);
    }

    this._tabsModel.changeData(Object.assign(
        this._tabsModel.getData(),
        {
          isTouched: false,
          touchX: null
        }
    ));
  }

  _handleToPropertiesButtonClick() {
    const index = this._tabsLinksViews.findIndex((view) => view.getHref() === `#${SectionId.PROPERTIES}`);

    if (index !== this._tabsModel.getData().activeTabIndex) {
      this._tabsModel.changeData(Object.assign(
          {},
          this._tabsModel.getData(),
          {
            activeTabIndex: index,
            oldActiveTabIndex: this._tabsModel.getData().activeTabIndex
          }
      ), ActionType.CHANGE);
    }

    this._scrollToElement.scroll({
      element: this._relatedSectionsViews[this._tabsModel.getData().activeTabIndex].getElement()
    });
  }

  _handleChangeData(action, payload) {
    switch (action) {
      case ActionType.CHANGE:
        this._tabsLinksViews[this._tabsModel.getData().activeTabIndex].setActive();
        this._tabsLinksViews[this._tabsModel.getData().oldActiveTabIndex].reset();

        const isScrolling = this._tabsLinksViews[this._tabsModel.getData().activeTabIndex].isScrolling();
        const isOldScrolling = this._tabsLinksViews[this._tabsModel.getData().oldActiveTabIndex].isScrolling();

        if (isScrolling) {
          if (!isOldScrolling) {
            this._relatedSectionsViews[this._tabsModel.getData().oldActiveTabIndex].hide();
          }

          this._scrollToElement.scroll({
            element: this._relatedSectionsViews[this._tabsModel.getData().activeTabIndex].getElement()
          });
        } else {
          if (!isOldScrolling) {
            this._relatedSectionsViews[this._tabsModel.getData().oldActiveTabIndex].hide();
          }

          this._animation.fadeIn(
              this._relatedSectionsViews[this._tabsModel.getData().activeTabIndex].getElement()
          );

          if (
            this._tabsLinksViews[this._tabsModel.getData().activeTabIndex].getHref() === `#${SectionId.DOCUMENTS}` &&
            !this._tabsModel.getData().docsInitialized) {
            this._initDocuments();
          }

          if (
            this._tabsLinksViews[this._tabsModel.getData().activeTabIndex].getHref() === `#${SectionId.REVIEWS}` &&
            !this._tabsModel.getData().reviewsInitialized) {
            this._initReviews();
          }
        }
        break;
      case ActionType.INIT_SLIDER:
        this._processInitSlider();
        break;
      case ActionType.DESTROY_SLIDER:
        this._processDestroySlider();
        break;
      case ActionType.SLIDE_NEXT:
        this._processSliderSlide();
        break;
      case ActionType.SLIDE_PREV:
        this._processSliderSlide();
        break;
      case ActionType.DISABLE_NAV_ITEM:
        this._disableNavItem(payload.navItemType);
        break;
      case ActionType.ENABLE_NAV_ITEM:
        this._enableNavItem(payload.navItemType);
        break;
    }
  }

  _handleWindowLoad() {
    this._changeSliderAppearance();
  }

  _handleWindowResize() {
    this._changeSliderAppearance();
  }
}
